import React, { useState, useEffect } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { Toaster, toast } from "react-hot-toast";
import Select from "react-select";
import { MultiValue, ActionMeta } from "react-select";
import {
  fetchAllowedMenus,
  fetchMeadiaSourceData,
  fetchAllSubMediaSource,
  UpdateCampaigns,
  fetchProjectData,
  fetchInCampaignProjectData,
  creatInCampaignAssignerProject,
  deteInCampaignAssignerProject,
  fetchCampaignTypes,
  fetchApiChannels,
  fetchUserData,
  assignTargetToUsers,
  fetchAllAssigedObjects,
} from "../Services/APIService";
import { ApiChannel } from "../types/apiChannel.types";
import OverlayLoader from "./OverlayLoader";

// Type definitions
interface MenuItem {
  menuID: number;
  menuName: string;
  menuUrl: string;
  icon: string;
  subMenuList?: any[];
}

interface UpdateCampaign {
  campaignId: number;
  campaignName: string;
  description: string;
  fromDate: Date;
  toDate: Date;
  isActive: boolean;
  mediaId: number;
  subMediumId: number;
  mediaIdentifierKey: string;
  campaignImage: string;
  modifiedBy: number;
  campaignTypeId?: number;
  isAssignTabShown?: boolean;
}

interface MediaSource {
  sourceId: number;
  sourceName: string;
  isActive: boolean;
}

interface SubMediaSource {
  id: number;
  name: string;
  mediaSourceID: number;
  isActive: boolean;
}

interface Project {
  projectID: number;
  projectName: string;
  isActive: boolean;
}

interface Campaigns {
  campaignId: number;
  campaignName: string;
  projectList: ProjectList[];
}

interface ProjectList {
  projectId: number;
  projectName: string;
  identifierKey: string;
}

interface SelectOption {
  value: number;
  label: string;
  projectData?: any;
  uniqueIdentity?: string;
  userData?: any;
}

interface ValidationErrors {
  campaignName?: string;
  description?: string;
  mediaSourceId?: string;
  subMediaSourceId?: string;
  fromDate?: string;
  toDate?: string;
  campaignImage?: string;
  listOfProjects?: string;
  uniqueIdentity?: string;
  campaignTypeId?: string;
  apiChannel?: string;
  listOfUsers?: string;
}

interface Assignment {
  id: number;
  project: SelectOption;
  uniqueIdentity: string;
  projectId: string | number;
  projectName: string;
  apiKey: string;
}

interface User {
  userId: number;
  userName: string;
  email: string;
  isActive?: boolean;
}

interface AssignmentData {
  tenantId: number;
  assignmentTypeId: number;
  assignmentObjectId: number;
  isAssigned: boolean;
  listOfUsers: { userId: number }[];
  createdBy: number;
}

interface UpdateCampaignModalProps {
  isOpen: boolean;
  onClose: () => void;
  campaignData: UpdateCampaign;
  onSubmit: (updatedCampaign: UpdateCampaign) => Promise<void>;
  onSuccess?: () => void;
}

interface ApiChannelOption {
  value: number;
  label: string;
  apiKey: string;
}

interface MediaSourceOption {
  value: number;
  label: string;
}

const UpdateCampaignModal: React.FC<UpdateCampaignModalProps> = ({
  isOpen,
  onClose,
  campaignData,
  onSubmit,
  onSuccess,
}) => {
  // State declarations
  const [isSidebarCollapsed, setIsSidebarCollapsed] = useState(false);
  const [allowedMenus, setAllowedMenus] = useState<MenuItem[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isLoadingCampaignTypes, setIsLoadingCampaignTypes] = useState(true);
  const [errors, setErrors] = useState<ValidationErrors>({});
  const [mediaSource, setMediaSource] = useState<MediaSource[]>([]);
  const [subMediaSource, setSubMediaSource] = useState<SubMediaSource[]>([]);
  const [campaignImageFile, setCampaignImageFile] = useState<File | null>(null);
  const [campaignImageChanged, setCampaignImageChanged] = useState(false);
  const [invalidFile, setInvalidFile] = useState<boolean>(false);
  const [allProjects, setAllProjects] = useState<Project[]>([]);
  const [allInCampaignProject, setInCampaignProject] = useState<Campaigns[]>(
    []
  );
  const [projectOptions, setProjectOptions] = useState<SelectOption[]>([]);
  const [selectedProjects, setSelectedProjects] = useState<SelectOption[]>([]);
  const [initialSelectedProjects, setInitialSelectedProjects] = useState<
    SelectOption[]
  >([]);
  const [isLoadingProjects, setIsLoadingProjects] = useState(true);
  const [isSubmittingAssignment, setIsSubmittingAssignment] = useState(false);
  const [activeTab, setActiveTab] = useState("home");
  const [uniqueIdentity, setUniqueIdentity] = useState("");
  const [assignments, setAssignments] = useState<Assignment[]>([]);
  const [campaignTypes, setCampaignTypes] = useState<
    { id: number; name: string; isActive: boolean }[]
  >([]);
  const [getAllApiChannel, setGetAllApiChannel] = useState<ApiChannel[]>([]);
  const [selectedApiChannel, setSelectedApiChannel] =
    useState<ApiChannelOption | null>(null);
  const [apiChannelOptions, setApiChannelOptions] = useState<
    ApiChannelOption[]
  >([]);
  const [mediaSourceOptions, setMediaSourceOptions] = useState<
    MediaSourceOption[]
  >([]);
  // User assignment states
  const [users, setUsers] = useState<User[]>([]);
  const [userOptions, setUserOptions] = useState<SelectOption[]>([]);
  const [selectedUsers, setSelectedUsers] = useState<SelectOption[]>([]);
  const [initialAssignedUsers, setInitialAssignedUsers] = useState<number[]>(
    []
  );
  const [isLoadingUsers, setIsLoadingUsers] = useState(true);
  const [isLoadingAssignedUsers, setIsLoadingAssignedUsers] = useState(true);

  const navigate = useNavigate();
  const location = useLocation();

  const [formData, setFormData] = useState({
    campaignName: "",
    mediaSourceId: "",
    subMediaSourceId: "",
    fromDate: "",
    toDate: "",
    description: "",
    campaignImage: "",
    isActive: true,
    campaignTypeId: "",
    apiChannelId: "",
  });

  // Helper function to format date for datetime-local input
  const formatDateForInput = (date: Date | string): string => {
    const dateObj = typeof date === "string" ? new Date(date) : date;
    if (!dateObj || isNaN(dateObj.getTime())) return "";

    const year = dateObj.getFullYear();
    const month = String(dateObj.getMonth() + 1).padStart(2, "0");
    const day = String(dateObj.getDate()).padStart(2, "0");
    const hours = String(dateObj.getHours()).padStart(2, "0");
    const minutes = String(dateObj.getMinutes()).padStart(2, "0");

    return `${year}-${month}-${day}T${hours}:${minutes}`;
  };

  // Menu handling effect
  useEffect(() => {
    const getMenus = async () => {
      setIsLoading(true);
      try {
        const menuID = location.state?.menuID;

        if (menuID === undefined || menuID === null || !menuID) {
          const currentUrl = window.location.pathname;
          const menuList = await fetchAllowedMenus();
          let foundMenuID: number | null = null;

          if (menuList && menuList.length > 0) {
            for (const menu of menuList) {
              if (menu.subMenuList && Array.isArray(menu.subMenuList)) {
                for (const subMenu of menu.subMenuList) {
                  const subMenuItem = subMenu as any;
                  if (subMenuItem.subMenuUrl === currentUrl) {
                    foundMenuID = menu.menuID;
                    localStorage.setItem(
                      "activeMenuID",
                      foundMenuID.toString()
                    );
                    break;
                  }
                }
              }
              if (foundMenuID) break;
            }
          }

          if (foundMenuID !== null) {
            localStorage.setItem("activeMenuID", foundMenuID.toString());
            console.log("Found and stored menuID:", foundMenuID);
          } else {
            localStorage.removeItem("activeMenuID");
          }
        } else {
          const previousMenuID = localStorage.getItem("activeMenuID");
          const menuIDString = menuID.toString();
          if (menuIDString !== previousMenuID || menuID === null) {
            localStorage.setItem("activeMenuID", menuIDString);
          } else {
            localStorage.removeItem("activeMenuID");
          }
        }
      } catch (error) {
        console.error("Error fetching menus:", error);
      } finally {
        setIsLoading(false);
      }
    };

    getMenus();
  }, [location.state]);

  // Data fetching effect
  useEffect(() => {
    const fetchData = async () => {
      setIsLoadingProjects(true);
      setIsLoadingUsers(true);
      setIsLoadingAssignedUsers(true);
      try {
        const [
          mediaSourceResponse,
          subMediaSourceResponse,
          allProjectsResponse,
          allInCampaign,
          allApiChannelsResponse,
          usersResponse,
          assignedUsersResponse,
        ] = await Promise.all([
          fetchMeadiaSourceData(),
          fetchAllSubMediaSource(),
          fetchProjectData(),
          fetchInCampaignProjectData(),
          fetchApiChannels(),
          fetchUserData(),
          fetchAllAssigedObjects(campaignData.campaignId, 1),
        ]);

        setMediaSource(mediaSourceResponse || []);
        setSubMediaSource(subMediaSourceResponse || []);
        setAllProjects(allProjectsResponse || []);
        setInCampaignProject(allInCampaign || []);
        setGetAllApiChannel(allApiChannelsResponse || []);

        const projectOptions = allProjectsResponse.map((project) => ({
          value: project.projectID,
          label: project.projectName,
          projectData: project,
        }));
        setProjectOptions(projectOptions);

        const activeMediaSource = mediaSourceResponse.filter(
          (source) => source.isActive === true
        );
        const mediaOption = activeMediaSource.map((source) => ({
          value: source.sourceId,
          label: source.sourceName,
        }));
        setMediaSourceOptions(mediaOption);

        const apiChannelOptions = allApiChannelsResponse.apiChannels.map(
          (channel: any) => ({
            value: channel.id,
            label: channel.channelName,
            apiKey: channel.apiKey,
          })
        );
        setApiChannelOptions(apiChannelOptions);

        const activeUsers = usersResponse.filter(
          (user: User) => user.isActive === true
        );
        setUsers(activeUsers);
        const userOptions = activeUsers.map((user) => ({
          value: user.userId,
          label: `${user.userName} (${user.email})`,
          userData: user,
        }));
        setUserOptions(userOptions);

        const assignedUsersData = assignedUsersResponse.map((user) => ({
          userId: user.userId,
          isActive: true,
        }));
        const preSelectedUsers = userOptions.filter((option) =>
          assignedUsersData.some(
            (assignedUser) => assignedUser.userId === option.value
          )
        );
        setSelectedUsers(preSelectedUsers);
        const initialUserIds = assignedUsersData.map((user) => user.userId);
        setInitialAssignedUsers(initialUserIds);

        if (campaignData && allInCampaign.length > 0) {
          const campaign = allInCampaign.find(
            (c) => c.campaignId === campaignData.campaignId
          );

          if (campaign && campaign.projectList) {
            const selected = projectOptions.filter((option) =>
              campaign.projectList.some(
                (proj) => proj.projectId === option.value
              )
            );

            setSelectedProjects(selected);
            setInitialSelectedProjects(selected);

            if (campaign.projectList.length > 0) {
              const existingAssignments: Assignment[] =
                campaign.projectList.map((proj: any, index: number) => ({
                  id: Date.now() + index,
                  project: {
                    value: proj.projectId,
                    label: proj.projectName,
                  } as SelectOption,
                  uniqueIdentity:
                    proj.identifierKey ||
                    `${proj.projectName}-${proj.projectId}`,
                  projectId: proj.projectId,
                  projectName: proj.projectName,
                  apiKey: proj.identifierKey || "",
                }));

              setAssignments(existingAssignments);
            }
          }
        }
      } catch (error) {
        console.error("Error fetching data:", error);
        toast.error("Failed to load required data");
      } finally {
        setIsLoadingProjects(false);
        setIsLoadingUsers(false);
        setIsLoadingAssignedUsers(false);
      }
    };

    if (isOpen) {
      fetchData();
    }
  }, [campaignData, isOpen]);

  // Campaign data initialization effect
  useEffect(() => {
    if (campaignData && campaignTypes.length > 0) {
      const validCampaignType = campaignTypes.find(
        (type) => type.id === campaignData.campaignTypeId
      );
      const selectedApiChannel = apiChannelOptions.find(
        (channel) => channel.apiKey === campaignData.mediaIdentifierKey
      );
      setActiveTab(campaignData.isAssignTabShown ? "user" : "home");
      setFormData({
        campaignName: campaignData.campaignName || "",
        mediaSourceId: campaignData.mediaId?.toString() || "",
        subMediaSourceId: "",
        fromDate: formatDateForInput(campaignData.fromDate),
        toDate: formatDateForInput(campaignData.toDate),
        description: campaignData.description || "",
        campaignImage: campaignData.campaignImage || "",
        isActive: campaignData.isActive,
        campaignTypeId: validCampaignType
          ? campaignData.campaignTypeId?.toString() || ""
          : "",
        apiChannelId: selectedApiChannel
          ? selectedApiChannel.value.toString()
          : "",
      });

      setSelectedApiChannel(selectedApiChannel || null);

      if (subMediaSource.length > 0 && campaignData.mediaId) {
        const validSubMedia = subMediaSource.find(
          (sub) =>
            sub.mediaSourceID === campaignData.mediaId &&
            sub.id === campaignData.subMediumId
        );
        if (validSubMedia) {
          setFormData((prev) => ({
            ...prev,
            subMediaSourceId: campaignData.subMediumId?.toString() || "",
          }));
        }
      }
    }
  }, [campaignData, campaignTypes, subMediaSource, apiChannelOptions]);

  // Fetch campaign types
  useEffect(() => {
    const fetchTypes = async () => {
      setIsLoadingCampaignTypes(true);
      try {
        const response = await fetchCampaignTypes();
        setCampaignTypes(
          response.campaignTypes?.filter((type: any) => type.isActive) || []
        );
      } catch (error) {
        console.error("Error fetching campaign types:", error);
        setCampaignTypes([]);
        toast.error("Failed to load campaign types");
      } finally {
        setIsLoadingCampaignTypes(false);
      }
    };
    if (isOpen) {
      fetchTypes();
    }
  }, [isOpen]);

  // File handling
  const fileToBase64 = (file: File): Promise<string> => {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.readAsDataURL(file);
      reader.onload = () => {
        const base64String = reader.result as string;
        const base64Content = base64String.split(",")[1];
        resolve(base64Content);
      };
      reader.onerror = (error) => reject(error);
    });
  };

  const validateFileExtension = (fileName: string): boolean => {
    const validExtensions = [".jpg", ".jpeg", ".png"];
    const fileExtension = fileName
      .slice(fileName.lastIndexOf("."))
      .toLowerCase();
    return validExtensions.includes(fileExtension);
  };

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files && e.target.files[0];

    if (file) {
      const isValidExtension = validateFileExtension(file.name);

      if (!isValidExtension) {
        setErrors((prevErrors) => ({
          ...prevErrors,
          campaignImage: "Only .jpeg, .jpg, and .png files are allowed",
        }));
        setInvalidFile(true);
        setCampaignImageChanged(true);
        setCampaignImageFile(file);
      } else {
        setCampaignImageFile(file);
        setCampaignImageChanged(true);
        setInvalidFile(false);
        setErrors((prevErrors) => ({
          ...prevErrors,
          campaignImage: undefined,
        }));
      }
    } else {
      setCampaignImageFile(null);
      setInvalidFile(false);
      setErrors((prevErrors) => ({ ...prevErrors, campaignImage: undefined }));
    }
  };

  const handleRemoveImage = () => {
    setCampaignImageFile(null);
    setCampaignImageChanged(false);
    setInvalidFile(false);
    setErrors((prevErrors) => ({ ...prevErrors, campaignImage: undefined }));
    const fileInput = document.getElementById(
      "campaignImageInput"
    ) as HTMLInputElement;
    if (fileInput) {
      fileInput.value = "";
    }
  };

  // Form handling
  const handleChange = (
    e: React.ChangeEvent<
      HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement
    >
  ) => {
    const { name, value, type } = e.target;

    if (type === "checkbox") {
      const checked = (e.target as HTMLInputElement).checked;
      setFormData((prevFormData) => ({
        ...prevFormData,
        [name]: checked,
      }));
      if (errors[name as keyof ValidationErrors]) {
        setErrors((prev) => ({ ...prev, [name]: undefined }));
      }
      return;
    }

    if (value.startsWith(" ")) return;

    setFormData((prevFormData) => {
      const updatedFormData = { ...prevFormData, [name]: value };
      if (name === "mediaSourceId") {
        updatedFormData.subMediaSourceId = "";
      }
      return updatedFormData;
    });

    if (errors[name as keyof ValidationErrors]) {
      setErrors((prev) => ({ ...prev, [name]: undefined }));
    }
  };

  const validateForm = (): boolean => {
    const newErrors: ValidationErrors = {};
    let isValid = true;

    if (!formData.campaignName.trim()) {
      newErrors.campaignName = "Campaign name is required";
      isValid = false;
    }
    if (!formData.campaignTypeId) {
      newErrors.campaignTypeId = "Campaign type is required";
      isValid = false;
    }
    if (!formData.mediaSourceId) {
      newErrors.mediaSourceId = "Media Source is required";
      isValid = false;
    }
    if (!formData.subMediaSourceId) {
      newErrors.subMediaSourceId = "Sub Media Source is required";
      isValid = false;
    }
    if (!formData.apiChannelId) {
      newErrors.apiChannel = "API Channel is required";
      isValid = false;
    }
    if (!formData.fromDate) {
      newErrors.fromDate = "From Date is required";
      isValid = false;
    }
    if (!formData.toDate) {
      newErrors.toDate = "To Date is required";
      isValid = false;
    }
    if (formData.fromDate && formData.toDate) {
      const fromDateObj = new Date(formData.fromDate);
      const toDateObj = new Date(formData.toDate);
      if (toDateObj < fromDateObj) {
        newErrors.toDate = "To Date cannot be earlier than From Date";
        isValid = false;
      }
    }
    if (invalidFile) {
      newErrors.campaignImage = "Only .jpeg, .jpg, and .png files are allowed";
      isValid = false;
    }

    setErrors(newErrors);
    return isValid;
  };

  const validateAssignmentData = (): boolean => {
    const newErrors: ValidationErrors = {};
    let isValid = true;

    if (!selectedProjects || selectedProjects.length === 0) {
      newErrors.listOfProjects = "Please select at least one project";
      isValid = false;
    }

    if (!uniqueIdentity.trim()) {
      newErrors.uniqueIdentity = "Please enter a unique identifier";
      isValid = false;
    }

    setErrors(newErrors);
    return isValid;
  };

  const validateUserAssignment = (): boolean => {
    const newErrors: ValidationErrors = {};
    let isValid = true;

    if (!selectedUsers || selectedUsers.length === 0) {
      newErrors.listOfUsers = "Please select at least one user";
      isValid = false;
    }

    setErrors(newErrors);
    return isValid;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setErrors({});
    const isValid = validateForm();

    if (!isValid) {
      return;
    }

    const userId = localStorage.getItem("UserId");
    const selectedChannel = apiChannelOptions.find(
      (channel) => channel.value.toString() === formData.apiChannelId
    );

    try {
      let campaignImageData = formData.campaignImage;

      if (campaignImageChanged && campaignImageFile) {
        campaignImageData = await fileToBase64(campaignImageFile);
      }

      if (!campaignData) {
        toast.error("Campaign data not found");
        return;
      }

      const updateData: UpdateCampaign = {
        campaignId: campaignData.campaignId,
        campaignName: formData.campaignName,
        description: formData.description,
        fromDate: new Date(formData.fromDate),
        toDate: new Date(formData.toDate),
        isActive: formData.isActive,
        mediaId: parseInt(formData.mediaSourceId),
        subMediumId: parseInt(formData.subMediaSourceId || "0"),
        mediaIdentifierKey: selectedChannel ? selectedChannel.apiKey : "",
        campaignImage: campaignImageData,
        modifiedBy: userId ? parseInt(userId) : 0,
        campaignTypeId: formData.campaignTypeId
          ? parseInt(formData.campaignTypeId)
          : undefined,
      };

      const result = await UpdateCampaigns(updateData);

      if (result.isUpdated) {
        toast.success("Campaign updated successfully!");
        resetForm();
        if (onSuccess) {
          onSuccess();
        }
        onClose();
        setTimeout(() => {
          navigate("/campaignView", {
            state: { refresh: true, timestamp: Date.now() },
            replace: true,
          });
        }, 500);
      } else {
        toast.error(
          result.message || "With This Campaign Name Campaign Already Exist"
        );
      }
    } catch (error) {
      console.error("Error updating campaign:", error);
      toast.error("An error occurred while updating the campaign");
    }
  };

  const handleMultiSelectChange = (
    newValue: MultiValue<SelectOption>,
    actionMeta: ActionMeta<SelectOption>
  ) => {
    if (!newValue || newValue.length === 0) {
      setSelectedProjects([]);
      return;
    }

    const selectedOptions = Array.isArray(newValue) ? newValue : [newValue];
    setSelectedProjects(selectedOptions);

    if (errors.listOfProjects) {
      setErrors({ ...errors, listOfProjects: undefined });
    }
  };

  const handleUniqueIdentityChange = (
    e: React.ChangeEvent<HTMLInputElement>
  ) => {
    setUniqueIdentity(e.target.value);
    if (errors.uniqueIdentity) {
      setErrors((prev) => ({ ...prev, uniqueIdentity: "" }));
    }
  };

  const handleAddAssignment = () => {
    const newErrors: ValidationErrors = {};

    if (!selectedProjects || selectedProjects.length === 0) {
      newErrors.listOfProjects = "Please select at least one project";
    }

    if (!uniqueIdentity.trim()) {
      newErrors.uniqueIdentity = "Please enter a unique identifier";
    }

    const isDuplicateIdentity = assignments.some(
      (assignment) => assignment.uniqueIdentity === uniqueIdentity.trim()
    );

    if (isDuplicateIdentity) {
      newErrors.uniqueIdentity = "This unique identifier already exists";
    }

    if (Object.keys(newErrors).length > 0) {
      setErrors(newErrors);
      return;
    }

    const newAssignments: Assignment[] = selectedProjects.map((project) => ({
      id: Date.now() + Math.random(),
      project: project,
      uniqueIdentity: uniqueIdentity.trim(),
      projectId: project.value,
      projectName: project.label,
      apiKey: uniqueIdentity.trim(),
    }));

    const existingProjectIds = assignments.map((a) => a.projectId);
    const filteredNewAssignments = newAssignments.filter(
      (newAssignment) => !existingProjectIds.includes(newAssignment.projectId)
    );

    if (filteredNewAssignments.length === 0) {
      toast.error("Selected projects are already assigned");
      return;
    }

    setAssignments((prevAssignments) => [
      ...prevAssignments,
      ...filteredNewAssignments,
    ]);

    toast.success(
      `${filteredNewAssignments.length} project(s) assigned successfully`
    );

    setSelectedProjects([]);
    setUniqueIdentity("");
    setErrors({});
  };

  const handleAssignmentSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!assignments || assignments.length === 0) {
      toast.error("Please add at least one project assignment");
      return;
    }

    try {
      setIsSubmittingAssignment(true);

      const tenantId = localStorage.getItem("tenantId");
      const userId = localStorage.getItem("UserId");

      if (!tenantId || !userId) {
        throw new Error("Required session data not found");
      }

      if (initialSelectedProjects.length > 0) {
        const deleteData = {
          tenantId: parseInt(tenantId),
          campaignId: campaignData.campaignId,
          projectList: initialSelectedProjects.map((project) => ({
            projectId: project.value,
          })),
        };

        const removeResult = await deteInCampaignAssignerProject(deleteData);
        if (!removeResult.isUpdated) {
          throw new Error(
            removeResult.message || "Failed to unassign projects"
          );
        }
      }

      const createData = {
        tenantId: parseInt(tenantId),
        campaignId: campaignData.campaignId,
        projectList: assignments.map((assignment) => ({
          projectId: parseInt(assignment.projectId.toString()),
          identifierKey: assignment.apiKey,
        })),
        createdBy: parseInt(userId),
      };

      const addResult = await creatInCampaignAssignerProject(createData);

      if (!addResult.isCreated) {
        throw new Error(addResult.message || "Failed to assign new projects");
      }

      toast.success("Campaign Project updated successfully");

      const updatedSelectedProjects = assignments.map(
        (assignment) => assignment.project
      );
      setInitialSelectedProjects(updatedSelectedProjects);

      resetForm();
      if (onSuccess) {
        onSuccess();
      }
      onClose();
    } catch (error) {
      console.error("Error updating campaign project:", error);
      toast.error(
        error instanceof Error
          ? error.message
          : "An error occurred while updating campaign project"
      );
    } finally {
      setIsSubmittingAssignment(false);
    }
  };

  const handleRemoveAssignment = (assignmentId: number) => {
    setAssignments((prev) =>
      prev.filter((assignment) => assignment.id !== assignmentId)
    );
  };

  const handleUserMultiSelectChange = (
    newValue: MultiValue<SelectOption>,
    actionMeta: ActionMeta<SelectOption>
  ) => {
    if (!newValue || newValue.length === 0) {
      setSelectedUsers([]);
      return;
    }

    const selectedOptions = Array.isArray(newValue) ? newValue : [newValue];
    setSelectedUsers(selectedOptions);

    if (errors.listOfUsers) {
      setErrors({ ...errors, listOfUsers: undefined });
    }
  };

  const handleUserAssignmentSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!validateUserAssignment()) {
      return;
    }

    try {
      setIsSubmittingAssignment(true);

      const tenantId = localStorage.getItem("tenantId");
      const userId = localStorage.getItem("UserId");

      if (!tenantId || !userId) {
        throw new Error("Required session data not found");
      }

      const currentSelectedUserIds = selectedUsers.map((user) => user.value);
      const usersToUnassign = initialAssignedUsers.filter(
        (userId) => !currentSelectedUserIds.includes(userId)
      );
      const usersToAssign = currentSelectedUserIds;

      if (usersToUnassign.length > 0) {
        const unassignData: AssignmentData = {
          tenantId: parseInt(tenantId),
          assignmentTypeId: 1,
          assignmentObjectId: campaignData.campaignId,
          isAssigned: false,
          listOfUsers: usersToUnassign.map((userId) => ({ userId })),
          createdBy: parseInt(userId),
        };

        const unassignResult = await assignTargetToUsers(unassignData);
        if (!unassignResult.isUpdated) {
          throw new Error(unassignResult.message || "Failed to unassign users");
        }
      }

      if (usersToAssign.length > 0) {
        const assignData: AssignmentData = {
          tenantId: parseInt(tenantId),
          assignmentTypeId: 1,
          assignmentObjectId: campaignData.campaignId,
          isAssigned: true,
          listOfUsers: usersToAssign.map((userId) => ({ userId })),
          createdBy: parseInt(userId),
        };

        const assignResult = await assignTargetToUsers(assignData);
        if (!assignResult.isUpdated) {
          throw new Error(assignResult.message || "Failed to assign users");
        }
      }

      toast.success("Assignments updated successfully");

      setInitialAssignedUsers(currentSelectedUserIds);

      resetForm();
      if (onSuccess) {
        onSuccess();
      }
      onClose();
    } catch (error) {
      console.error("Error updating user assignments:", error);
      toast.error(
        error instanceof Error
          ? error.message
          : "An error occurred while updating assignments"
      );
    } finally {
      setIsSubmittingAssignment(false);
    }
  };

  const handleTabClick = (tabId: string) => {
    setActiveTab(tabId);
  };

  const resetForm = () => {
    setFormData({
      campaignName: "",
      mediaSourceId: "",
      subMediaSourceId: "",
      fromDate: "",
      toDate: "",
      description: "",
      campaignImage: "",
      isActive: true,
      campaignTypeId: "",
      apiChannelId: "",
    });
    setCampaignImageFile(null);
    setCampaignImageChanged(false);
    setInvalidFile(false);
    setErrors({});
    setAssignments([]);
    setSelectedProjects([]);
    setUniqueIdentity("");
    setSelectedApiChannel(null);
    setSelectedUsers([]);
  };

  const handleApiChannelChange = (selectedOption: ApiChannelOption | null) => {
    setSelectedApiChannel(selectedOption);
    setFormData((prev) => ({
      ...prev,
      apiChannelId: selectedOption ? selectedOption.value.toString() : "",
    }));
    if (selectedOption) {
      setErrors((prev) => ({ ...prev, apiChannel: undefined }));
    }
  };

  const handleMediaSourceChange = (
    selectedOption: MediaSourceOption | null
  ) => {
    setFormData((prev) => ({
      ...prev,
      mediaSourceId: selectedOption ? selectedOption.value.toString() : "",
    }));
    if (selectedOption) {
      setErrors((prev) => ({ ...prev, mediaSourceId: undefined }));
    }
  };

  const getSelectedMediaOption = (): MediaSourceOption | null => {
    return (
      mediaSourceOptions.find(
        (option) => option.value === campaignData?.mediaId
      ) || null
    );
  };

  const requiredField = <span className="text-danger ms-1">*</span>;

  const iconButtonStyle: React.CSSProperties = {
    top: "36%",
    right: "10px",
    transform: "translateY(-50%)",
    background: "none",
    border: "none",
    fontSize: "16px",
    lineHeight: "1",
    color: "#495057",
    position: "absolute",
  };

  const handleClose = () => {
    resetForm();
    onClose();
  };

  if (!isOpen) return null;

  return (
    <>
      <div
        className={`modal-backdrop-custom ${isOpen ? "open" : "closed"}`}
        onClick={handleClose}
      ></div>
      <div className={`modal-custom ${isOpen ? "open" : "closed"}`}>
        <div className="modal-content ProjectEditFormModal">
          <div className="modal-header-custom">
            <h5 className="modal-title-custom">Edit Campaign</h5>
            <button
              type="button"
              className="btn-close-custom"
              onClick={handleClose}
            >
              <span className="icon-close"></span>
            </button>
          </div>
          <ul
            className="nav nav-tabs pop-up-tabHeader"
            id="myTab"
            role="tablist"
            style={{ borderBottom: "1px solid rgb(245, 235, 240)" }}
          >
            <li className="nav-item" role="presentation">
              <button
                className={`nav-link card-title tab-btn pop-up-tabHeaderbtn ${
                  activeTab === "home" ? "active" : ""
                }`}
                id="home-tab"
                onClick={() => handleTabClick("home")}
                type="button"
                role="tab"
                aria-controls="home-tab-pane"
                aria-selected={activeTab === "home"}
              >
                Campaign Details
              </button>
            </li>
            <li className="nav-item" role="presentation">
              <button
                className={`nav-link card-title tab-btn pop-up-tabHeaderbtn ${
                  activeTab === "contact" ? "active" : ""
                }`}
                id="contact-tab"
                onClick={() => handleTabClick("contact")}
                type="button"
                role="tab"
                aria-controls="contact-tab-pane"
                aria-selected={activeTab === "contact"}
              >
                Project Mapping
              </button>
            </li>
            <li className="nav-item" role="presentation">
              <button
                className={`nav-link card-title tab-btn pop-up-tabHeaderbtn ${
                  activeTab === "user" ? "active" : ""
                }`}
                id="user-tab"
                onClick={() => handleTabClick("user")}
                type="button"
                role="tab"
                aria-controls="user-tab-pane"
                aria-selected={activeTab === "user"}
              >
                Assigners Information
              </button>
            </li>
          </ul>
          <div className="modal-body-custom">
            <div className="card pop-up-card">
              <div className="tab-content card-body p-0" id="myTabContent">
                {/* Campaign Details Tab */}
                <div
                  className={`tab-pane fade ${
                    activeTab === "home" ? "show active" : ""
                  }`}
                  id="home-tab-pane"
                  role="tabpanel"
                  aria-labelledby="home-tab"
                  tabIndex={0}
                >
                  <form onSubmit={handleSubmit}>
                    <div className="row">
                      <div className="col-12 col-lg-12 mb-3 input-group-custom">
                        <label className="form-label" htmlFor="campaignName">
                          Campaign Name{requiredField}
                        </label>
                        <div className="input-with-icon">
                          <span className="input-icon icon-campaign"></span>
                          <input
                            name="campaignName"
                            placeholder="Campaign Name"
                            value={formData.campaignName}
                            onChange={handleChange}
                            className={`form-control-custom ${
                              errors.campaignName ? "is-invalid" : ""
                            }`}
                          />
                          {errors.campaignName && (
                            <div className="invalid-feedback text-danger">
                              {errors.campaignName}
                            </div>
                          )}
                        </div>
                      </div>
                      <div className="col-12 col-lg-12 mb-3 input-group-custom">
                        <label className="form-label" htmlFor="campaignTypeId">
                          Campaign Type{requiredField}
                        </label>
                        {campaignTypes.length === 0 &&
                        !isLoadingCampaignTypes ? (
                          <div className="text-muted">
                            No campaign types available
                          </div>
                        ) : (
                          <select
                            name="campaignTypeId"
                            value={formData.campaignTypeId || ""}
                            onChange={handleChange}
                            className={`form-select form-control-lg ${
                              errors.campaignTypeId ? "is-invalid" : ""
                            }`}
                            disabled={isLoadingCampaignTypes}
                          >
                            <option value="">Select Campaign Type</option>
                            {campaignTypes.map((type) => (
                              <option key={type.id} value={type.id.toString()}>
                                {type.name}
                              </option>
                            ))}
                          </select>
                        )}
                        {errors.campaignTypeId && (
                          <div className="invalid-feedback text-danger">
                            {errors.campaignTypeId}
                          </div>
                        )}
                      </div>
                      <div className="col-12 col-lg-12 mb-3 input-group-custom">
                        <label className="form-label" htmlFor="mediaSourceId">
                          Media Source{requiredField}
                        </label>
                        {isLoadingProjects ? (
                          <div className="text-muted">
                            Loading Media Sources...
                          </div>
                        ) : (
                          <Select
                            options={mediaSourceOptions}
                            value={getSelectedMediaOption()}
                            onChange={handleMediaSourceChange}
                            className={`basic-single-select ${
                              errors.mediaSourceId ? "is-invalid" : ""
                            }`}
                            classNamePrefix="select"
                            placeholder="Select State"
                            isSearchable={true}
                            isClearable={true}
                            id="mediaSourceId"
                            name="mediaSourceId"
                          />
                        )}
                        {errors.mediaSourceId && (
                          <div className="invalid-feedback text-danger">
                            {errors.mediaSourceId}
                          </div>
                        )}
                      </div>
                      <div className="col-12 col-lg-12 mb-3 input-group-custom">
                        <label
                          className="form-label"
                          htmlFor="subMediaSourceId"
                        >
                          Sub Media Source{requiredField}
                        </label>
                        {isLoadingProjects ? (
                          <div className="text-muted">
                            Loading Sub Media Sources...
                          </div>
                        ) : (
                          <select
                            name="subMediaSourceId"
                            value={formData.subMediaSourceId || ""}
                            onChange={handleChange}
                            className={`form-select form-control-lg ${
                              errors.subMediaSourceId ? "is-invalid" : ""
                            }`}
                          >
                            <option value="">Select Sub Media Source</option>
                            {subMediaSource
                              .filter(
                                (sub) =>
                                  sub.mediaSourceID ===
                                  parseInt(formData.mediaSourceId)
                              )
                              .map((sub) => (
                                <option key={sub.id} value={sub.id}>
                                  {sub.name}
                                </option>
                              ))}
                          </select>
                        )}
                        {errors.subMediaSourceId && (
                          <div className="invalid-feedback text-danger">
                            {errors.subMediaSourceId}
                          </div>
                        )}
                      </div>
                      <div className="col-12 col-lg-12 mb-3 input-group-custom">
                        <label className="form-label" htmlFor="apiChannelId">
                          API Channel{requiredField}
                        </label>
                        <Select
                          options={apiChannelOptions}
                          value={selectedApiChannel}
                          onChange={handleApiChannelChange}
                          isClearable
                          isSearchable
                          className={`basic-single-select ${
                            errors.apiChannel ? "is-invalid" : ""
                          }`}
                          classNamePrefix="select"
                          placeholder="Select API channel"
                          noOptionsMessage={() => "No API channels available"}
                        />
                        {errors.apiChannel && (
                          <div className="text-danger mt-1 small">
                            {errors.apiChannel}
                          </div>
                        )}
                        <div className="form-text">
                          Select an API channel for this campaign
                        </div>
                      </div>
                      <div className="col-12 col-lg-6 mb-3 input-group-custom">
                        <label className="form-label" htmlFor="fromDate">
                          From Date{requiredField}
                        </label>
                        <input
                          type="datetime-local"
                          name="fromDate"
                          value={formData.fromDate}
                          onChange={handleChange}
                          className={`form-control-custom1 form-control-custom-lg ${
                            errors.fromDate ? "is-invalid" : ""
                          }`}
                        />
                        {errors.fromDate && (
                          <div className="invalid-feedback text-danger">
                            {errors.fromDate}
                          </div>
                        )}
                      </div>
                      <div className="col-12 col-lg-6 mb-3 input-group-custom">
                        <label className="form-label" htmlFor="toDate">
                          To Date{requiredField}
                        </label>
                        <input
                          type="datetime-local"
                          name="toDate"
                          value={formData.toDate}
                          onChange={handleChange}
                          className={`form-control-custom1 form-control-custom-lg ${
                            errors.toDate ? "is-invalid" : ""
                          }`}
                        />
                        {errors.toDate && (
                          <div className="invalid-feedback text-danger">
                            {errors.toDate}
                          </div>
                        )}
                      </div>
                      <div className="col-12 col-lg-12 mb-3 input-group-custom">
                        <label
                          className="form-label"
                          htmlFor="campaignImageInput"
                        >
                          Campaign Image
                        </label>
                        <div className="position-relative">
                          <input
                            id="campaignImageInput"
                            type="file"
                            className={`form-control-custom1 form-control-custom-lg ${
                              errors.campaignImage ? "is-invalid" : ""
                            }`}
                            onChange={handleFileChange}
                            accept="image/jpeg,image/jpg,image/png"
                            style={{
                              paddingRight: campaignImageFile ? "30px" : "12px",
                            }}
                          />
                          {formData.campaignImage &&
                            !campaignImageChanged &&
                            !campaignImageFile && (
                              <div className="form-text text-success">
                                Campaign Image already uploaded
                              </div>
                            )}
                          {campaignImageFile && (
                            <>
                              <div className="form-text">
                                Selected: {campaignImageFile.name}
                              </div>
                              <button
                                type="button"
                                className="position-absolute p-0"
                                style={iconButtonStyle}
                                onClick={handleRemoveImage}
                              >
                                ×
                              </button>
                            </>
                          )}
                        </div>
                        {errors.campaignImage && (
                          <div className="invalid-feedback text-danger d-block">
                            {errors.campaignImage}
                          </div>
                        )}
                      </div>
                      <div className="col-12 col-lg-12 mb-3 input-group-custom">
                        <label className="form-label" htmlFor="description">
                          Description
                        </label>
                        <textarea
                          name="description"
                          placeholder="Description"
                          value={formData.description}
                          onChange={handleChange}
                          className={`form-control-custom-textarea ${
                            errors.description ? "is-invalid" : ""
                          }`}
                        />
                        {errors.description && (
                          <div className="invalid-feedback text-danger">
                            {errors.description}
                          </div>
                        )}
                      </div>
                      <div className="col-12 col-lg-6 mb-3 input-group-custom">
                        <div className="form-check">
                          <input
                            type="checkbox"
                            name="isActive"
                            checked={formData.isActive}
                            onChange={handleChange}
                            className="form-check-input"
                          />
                          <label className="form-check-label">Is Active</label>
                        </div>
                      </div>
                    </div>
                  </form>
                </div>
                {/* Project Mapping Tab */}
                <div
                  className={`tab-pane fade ${
                    activeTab === "contact" ? "show active" : ""
                  }`}
                  id="contact-tab-pane"
                  role="tabpanel"
                  aria-labelledby="contact-tab"
                  tabIndex={0}
                >
                  <form onSubmit={handleAssignmentSubmit}>
                    <div className="row">
                      <div className="col-12 col-lg-12 mb-3 input-group-custom">
                        <label className="form-label">
                          Assign Project{requiredField}
                        </label>
                        <Select
                          options={projectOptions}
                          value={selectedProjects}
                          onChange={handleMultiSelectChange}
                          isMulti
                          className={`basic-multi-select ${
                            errors.listOfProjects ? "is-invalid" : ""
                          }`}
                          classNamePrefix="select"
                          placeholder="Select project to campaign"
                          isLoading={isLoadingProjects}
                          noOptionsMessage={() => "No project available"}
                        />
                        {errors.listOfProjects && (
                          <div className="text-danger mt-1 small">
                            {errors.listOfProjects}
                          </div>
                        )}
                        <div className="form-text">
                          Select a project to assign to this campaign
                        </div>
                      </div>
                      <div className="col-12 col-lg-12 mb-3 input-group-custom">
                        <label className="form-label" htmlFor="uniqueIdentity">
                          Unique Identifier{requiredField}
                        </label>
                        <input
                          type="text"
                          name="uniqueIdentity"
                          placeholder="Enter unique identifier"
                          value={uniqueIdentity}
                          onChange={handleUniqueIdentityChange}
                          className={`form-control-custom ${
                            errors.uniqueIdentity ? "is-invalid" : ""
                          }`}
                        />
                        {errors.uniqueIdentity && (
                          <div className="invalid-feedback text-danger">
                            {errors.uniqueIdentity}
                          </div>
                        )}
                        <div className="form-text">
                          Enter a unique identifier for this assignment
                        </div>
                      </div>
                      <div className="col-12 col-lg-2 mb-3 d-flex align-items-end">
                        <button
                          type="button"
                          className="btn btn-success btn-custom-edcamp"
                          onClick={handleAddAssignment}
                        >
                          <i className="bi bi-plus-circle me-1"></i>Add
                        </button>
                      </div>
                    </div>
                  </form>
                  {assignments.length > 0 && (
                    <div className="mt-4">
                      <h6 className="form-label fw-bold">
                        Project Assignments
                      </h6>
                      <div className="table-responsive">
                        <table className="table table-bordered table-hover">
                          <thead className="table-light">
                            <tr>
                              <th scope="col">#</th>
                              <th scope="col">Project ID</th>
                              <th scope="col">Project Name</th>
                              <th scope="col">Unique Identifier</th>
                              <th scope="col" className="text-center">
                                Action
                              </th>
                            </tr>
                          </thead>
                          <tbody>
                            {assignments.map((assignment, index) => (
                              <tr key={assignment.id}>
                                <td>{index + 1}</td>
                                <td>
                                  <span>{assignment.projectId}</span>
                                </td>
                                <td>{assignment.projectName}</td>
                                <td>
                                  <span className="fw-semibold">
                                    {assignment.uniqueIdentity}
                                  </span>
                                </td>
                                <td className="text-center">
                                  <i
                                    className="bi bi-trash colorred"
                                    style={{ cursor: "pointer" }}
                                    onClick={() =>
                                      handleRemoveAssignment(assignment.id)
                                    }
                                    title="Remove assignment"
                                  ></i>
                                </td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                      <div className="text-muted small mb-4">
                        Total projects: {assignments.length}
                      </div>
                    </div>
                  )}
                </div>
                {/* User Mapping Tab */}
                <div
                  className={`tab-pane fade ${
                    activeTab === "user" ? "show active" : ""
                  }`}
                  id="user-tab-pane"
                  role="tabpanel"
                  aria-labelledby="user-tab"
                  tabIndex={0}
                >
                  <div className="modal-body-content-edit-campaign">
                    <form onSubmit={handleUserAssignmentSubmit}>
                      <div className="row">
                        <div className="col-12 col-lg-12 input-group-custom">
                          <label className="form-label">
                            Assign Users{requiredField}
                          </label>
                          <Select
                            options={userOptions}
                            value={selectedUsers}
                            onChange={handleUserMultiSelectChange}
                            isMulti
                            className={`basic-multi-select ${
                              errors.listOfUsers ? "is-invalid" : ""
                            }`}
                            classNamePrefix="select"
                            placeholder="Select users to assign"
                            isLoading={isLoadingUsers || isLoadingAssignedUsers}
                            noOptionsMessage={() => "No users available"}
                          />
                          {errors.listOfUsers && (
                            <div className="text-danger mt-1 small">
                              {errors.listOfUsers}
                            </div>
                          )}
                          <div className="form-text">
                            Select multiple users to assign to this campaign
                          </div>
                        </div>
                      </div>
                    </form>
                  </div>
                </div>
              </div>
            </div>
            <div className="modal-footer-custom">
              <div>
                {activeTab === "home" ? (
                  <>
                    <button
                      type="button"
                      className="btn-custom btn-cancel"
                      onClick={handleClose}
                    >
                      Cancel
                    </button>
                    <button
                      type="button"
                      className="btn-custom btn-primary-custom"
                      onClick={handleSubmit}
                    >
                      Update Campaign
                    </button>
                  </>
                ) : activeTab === "contact" ? (
                  <>
                    <button
                      type="button"
                      className="btn-custom btn-cancel"
                      onClick={handleClose}
                    >
                      Cancel
                    </button>
                    <button
                      type="button"
                      className="btn-custom btn-primary-custom"
                      onClick={handleAssignmentSubmit}
                      disabled={isSubmittingAssignment}
                    >
                      {isSubmittingAssignment ? "Saving..." : "Save"}
                    </button>
                  </>
                ) : (
                  <>
                    <button
                      type="button"
                      className="btn-custom btn-cancel"
                      onClick={handleClose}
                    >
                      Cancel
                    </button>
                    <button
                      type="button"
                      className="btn-custom btn-primary-custom"
                      onClick={handleUserAssignmentSubmit}
                      disabled={isSubmittingAssignment}
                    >
                      {isSubmittingAssignment ? "Saving..." : "Save"}
                    </button>
                  </>
                )}
              </div>
            </div>
          </div>
        </div>
        <OverlayLoader
          loading={
            isLoadingCampaignTypes ||
            isLoadingProjects ||
            isLoadingUsers ||
            isLoadingAssignedUsers
          }
          message="Bind Campaign Edit Data"
          submessage="This might take a moment..."
        />
      </div>
    </>
  );
};

export default UpdateCampaignModal;
